import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBus, 
  faSearch, 
  faMapMarkerAlt, 
  faBell, 
  faTicketAlt, 
  faChartLine, 
  faExclamationTriangle, 
  faTruck, 
  faRecycle,
  faWrench,
  faUserShield,
  faFileAlt,
  faGavel,
  faTrain,
  faSubway,
  faShip,
  faTaxi,
  faHeart,
  faUser,
  faPhone,
  faBullhorn, 
  faCalculator,
  faMapPin,
  faMessage
} from "@fortawesome/free-solid-svg-icons";
import BusModule from "./bus/BusModule";
import initialVehicles from "./bus/data/busseed";

export default function TindiHome() {
  const transportIcons = {
    "Bus": faBus,
    "Taxi/ Auto": faTaxi,
    "Metro": faSubway,
    "Local Trains": faTrain,
    "Ferry": faShip,
  };

  const [routeQuery, setRouteQuery] = useState("");
  const [selectedStakeholder, setSelectedStakeholder] = useState("Passenger");
  const [selectedTransportMode, setSelectedTransportMode] = useState("Bus"); // New: Transport mode selector
  const [vehicles, setVehicles] = useState(initialVehicles);
  const [activeVehicle, setActiveVehicle] = useState(null);
  const [showSeatMap, setShowSeatMap] = useState(false);
  const [showSOS, setShowSOS] = useState(false);
  const [notifications, setNotifications] = useState(["Welcome to Tindi — Trip Indicator"]);

  const [booking, setBooking] = useState({ route: "", seats: 1, vehicleId: null });
  const [bookingsHistory, setBookingsHistory] = useState([]);
  const [trafficStatus, setTrafficStatus] = useState({}); // For Driver

  const [fareFrom, setFareFrom] = useState("");
  const [fareTo, setFareTo] = useState("");
  const [calculatedFare, setCalculatedFare] = useState(null);
  const [favorites, setFavorites] = useState(["Churchgate Station", "Bandra Bus Stop"]);
  const [alerts, setAlerts] = useState([
    "Heavy rain expected; delays on Western Line",
    "BEST Bus strike update: Services resuming tomorrow"
  ]);

  const nearbyServices = {
    hotels: ["Taj Mahal Palace", "Oberoi"],
    hospitals: ["Lilavati Hospital", "Kokilaben Hospital"],
    attractions: ["Gateway of India", "Marine Drive"],
    airports: ["CSMIA Terminal 1 & 2"]
  };

  const helplines = {
    police: "100",
    medical: "108",
    railway: "139"
  };

  useEffect(() => {
    const iv = setInterval(() => {
      setVehicles(prev => {
        const idx = Math.floor(Math.random() * prev.length);
        const copy = [...prev];
        const v = { ...copy[idx] };

        if (["Local Train", "Metro", "Ferry", "Bus"].includes(v.type)) {
          const statuses = ["On Time", "Delay +5m", "Delay +10m"];
          v.status = statuses[Math.floor(Math.random() * statuses.length)];
          v.nextArrival = `${Math.floor(Math.random() * 10) + 1} min`;
          v.crowdPrediction = ["Low", "Moderate", "High"][Math.floor(Math.random() * 3)];
          v.delayForecast = ["Low risk", "Medium risk", "High risk"][Math.floor(Math.random() * 3)];
        }
        copy[idx] = v;
        return copy;
      });
      if (Math.random() > 0.8) {
        setAlerts(a => [`New Alert: ${["Rain delay", "Strike update", "Event traffic"][Math.floor(Math.random() * 3)]}`, ...a]);
      }
    }, 12000);
    return () => clearInterval(iv);
  }, []);

  const getFilteredVehicles = () => {
    let filtered = vehicles;
    if (selectedTransportMode !== "All" && selectedTransportMode !== "Fare Calculator" && selectedTransportMode !== "Favorites" && selectedTransportMode !== "Nearby Services" && selectedTransportMode !== "My Profile" && selectedTransportMode !== "Emergency") {
      filtered = filtered.filter(v => v.type.includes(selectedTransportMode.replace("/Auto", "")) || (selectedTransportMode === "Taxi/ Auto" && (v.type === "Taxi" || v.type === "Auto")));
    }
    switch (selectedStakeholder) {
      case "Passenger":
      case "Attendant":
        filtered = filtered.filter(v => ["Bus", "Local Train", "Metro", "Monorail", "Ferry", "Taxi", "Auto"].includes(v.type));
        break;
      case "Merchant":
        filtered = filtered.filter(v => v.type === "Cargo Van" || v.type === "Intercity Bus");
        break;
      case "Municipal Officer":
        filtered = filtered.filter(v => v.type === "Garbage Van" || v.type === "Bus");
        break;
      default:
        break;
    }
    return filtered.filter(v => 
      routeQuery.trim() === "" || 
      v.route.toLowerCase().includes(routeQuery.toLowerCase()) || 
      v.id.toLowerCase().includes(routeQuery.toLowerCase())
    );
  };

  const filteredVehicles = getFilteredVehicles();
  const addNotification = (msg) => setNotifications(n => [msg, ...n]);

  const handleOpenVehicle = (v) => {
    setActiveVehicle(v);
    setShowSeatMap(true);
  };

  const handleBookNow = () => {
    if (!booking.vehicleId) {
      addNotification("Select a vehicle to book");
      return;
    }
    const vehicle = vehicles.find(v => v.id === booking.vehicleId);
    const rec = { id: `BK-${Date.now()}`, vehicleId: vehicle.id, route: vehicle.route, seats: booking.seats, time: new Date().toLocaleString() };
    setBookingsHistory(h => [rec, ...h]);
    addNotification(`Booked ${rec.vehicleId} (${rec.route}) — ${rec.seats} seat(s)`);
    setBooking({ route: "", seats: 1, vehicleId: null });
  };

  const handleSOS = (v = null) => {
    setShowSOS(true);
    addNotification(`SOS triggered for ${v ? v.id : 'system'} — team alerted (dummy)`);
  };

  const handleSync = () => addNotification("📍 Location sync invoked (dummy)");

  const handleReport = () => addNotification("📊 Report generated (dummy)");

  const updateTrafficStatus = (vehicleId, newStatus) => {
    setVehicles(prev => prev.map(v => (v.id === vehicleId ? { ...v, status: newStatus } : v)));
    addNotification(`${vehicleId} status updated to ${newStatus}`);
  };

  const sellTicket = (vehicleId) => {
    setVehicles(prev => prev.map(v => (v.id === vehicleId ? { ...v, passengersOn: (v.passengersOn || 0) + 1 } : v)));
    addNotification(`Ticket sold on ${vehicleId} (dummy)`);
  };

  const calculateFare = () => {
    const baseFare = Math.floor(Math.random() * 100) + 10;
    setCalculatedFare({ total: baseFare, breakdown: `${selectedTransportMode}: ₹${baseFare}` });
    addNotification(`Fare from ${fareFrom} to ${fareTo}: ₹${baseFare} (AI optimized)`);
  };

  const toggleFavorite = (item) => {
    setFavorites(f => f.includes(item) ? f.filter(i => i !== item) : [...f, item]);
    addNotification(`${item} ${favorites.includes(item) ? 'removed from' : 'added to'} favorites`);
  };

  const handleAssignDriver = () => addNotification("Driver assigned (dummy)");
  const handleRepairStatus = () => addNotification("Repair status updated (dummy)");
  const handleFine = () => addNotification("Fine issued (dummy)");
  const handleCargoUpdate = () => addNotification("Cargo status updated (dummy)");
  const handleCollectionUpdate = () => addNotification("Collection logged (dummy)");

  const renderHeader = () => (
    <>
    <header className="flex items-center justify-between mb-2 flex-wrap gap-2">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center text-4xl font-bold">🛣️</div>
        <div>
          <h1 className="text-lg font-semibold text-start">Tindi</h1>
          <p className="text-xs text-gray-400">Trip Indicator</p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button className="w-8 p-2 rounded-md bg-gray-800 border border-gray-700 hover:bg-gray-700">
          <FontAwesomeIcon icon={faBell} />
        </button>
        <button className="w-8 p-2 rounded-md bg-gradient-to-br from-indigo-600 to-purple-600 text-white">
          <FontAwesomeIcon icon={faMapMarkerAlt} />
        </button>
      </div>
    </header>
    <div className="flex flex-row justify-between items-center gap-2 flex-wrap border-b border-blue-400 mb-2 mt-2 py-2">
        <div className="flex items-center gap-2 text-sm text-gray-300">
          <select value={selectedStakeholder} onChange={e => setSelectedStakeholder(e.target.value)} className="bg-gray-800 border border-gray-700 px-2 py-1 rounded text-sm">
            <option>Passenger</option>
            <option>Admin</option>
            <option>Fleet Manager</option>
            <option>Driver</option>
            <option>Attendant</option>
            <option>Mechanic</option>
            <option>Service Center</option>
            <option>Vigilance</option>
            <option>Merchant</option>
            <option>Municipal Officer</option>
          </select>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-300">
          Mode:
          <select value={selectedTransportMode} onChange={e => setSelectedTransportMode(e.target.value)} className="bg-gray-800 border border-gray-700 px-2 py-1 rounded text-sm">
            <option>Bus</option>
            <option>Taxi/ Auto</option>
            <option>Metro</option>
            <option>Local Trains</option>
            <option>Ferry</option>
            <option>All</option>
          </select>
        </div>
      </div>
    </>
  );

  const renderSearchAndStats = () => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 border-t border-blue-400">
      <div className="col-span-1 sm:col-span-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-800 px-3 py-2 mt-2 rounded w-full">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400 mr-2" />
            <input
              className="bg-transparent outline-none w-full text-sm"
              placeholder={`Search ${selectedTransportMode} (e.g., Churchgate, A → B)`}
              value={routeQuery}
              onChange={e => setRouteQuery(e.target.value)}
            />
          </div>
          <button className="hidden sm:inline-flex items-center gap-2 mt-2 px-2 py-1 w-20 bg-indigo-600 rounded" onClick={() => addNotification("Search executed")}>Search</button>
        </div>
        <div className="mt-3 grid grid-cols-3 lg:grid-cols-4 gap-3">
          <StatCard title={`${selectedTransportMode}`} value={filteredVehicles.length} icon={<FontAwesomeIcon icon={transportIcons[selectedTransportMode]} />} />
          <StatCard title="Alerts" value={alerts.length} icon={<FontAwesomeIcon icon={faBullhorn} />} />
          <StatCard title="Fare Calculator" icon={<FontAwesomeIcon icon={faCalculator} />} />
          <StatCard title="Spot Nearby " value={nearbyServices.length} icon={<FontAwesomeIcon icon={faMapPin} />} />
          <StatCard title="Favourites" value={favorites.length} icon={<FontAwesomeIcon icon={faHeart} />} />
          <StatCard title="My Profile" icon={<FontAwesomeIcon icon={faUser} />} />
          <StatCard title="Chat" value={34} icon={<FontAwesomeIcon icon={faMessage} />} />
          <StatCard title="Emergency" icon={<FontAwesomeIcon icon={faExclamationTriangle} />} />

          {selectedStakeholder === "Passenger" && (
            <StatCard title="Bookings" value={bookingsHistory.length} icon={<FontAwesomeIcon icon={faTicketAlt} />} />
          )}
          {(selectedStakeholder === "Admin" || selectedStakeholder === "Fleet Manager") && (
            <StatCard title="AI Predictions" value={"Active"} icon={<FontAwesomeIcon icon={faChartLine} />} />
          )}
        </div>
      </div>
      <QuickActions />
    </div>
  );

  const QuickActions = () => (
    <div className="col-span-1 mt-2">
      <div className="bg-gray-800 rounded p-2 h-full">
        <h3 className="text-sm text-yellow-200 mb-2 font-semibold ">Quick Actions</h3>
        <div className="grid grid-cols-2 gap-2 place-items-center ">
          {["Passenger", "Driver", "Fleet Manager", "Attendant"].includes(selectedStakeholder) && (
            <button className="w-48 text-sm text-left px-3 py-2 bg-gray-700 rounded flex items-center gap-3 text-white hover:bg-red-700 transition" onClick={() => handleSOS()}>
              <FontAwesomeIcon icon={faExclamationTriangle} /> Trigger SOS
            </button>
          )}
          {selectedStakeholder === "Driver" && (
            <button className="w-48 text-sm text-left px-3 py-2 bg-gray-700 rounded flex items-center gap-3 text-white hover:bg-blue-700 transition" onClick={handleSync}>
              <FontAwesomeIcon icon={faMapMarkerAlt} /> Sync Location
            </button>
          )}
          {(selectedStakeholder === "Admin" || selectedStakeholder === "Fleet Manager") && (
            <button className="w-48 text-sm text-left px-3 py-2 bg-gray-700 rounded flex items-center gap-3 text-white hover:bg-green-700 transition" onClick={handleReport}>
              <FontAwesomeIcon icon={faChartLine} /> Generate Report
            </button>
          )}
          {selectedStakeholder === "Fleet Manager" && (
            <button className="w-48 text-sm text-left px-3 py-2 bg-gray-700 rounded flex items-center gap-3 text-white hover:bg-indigo-700 transition" onClick={handleAssignDriver}>
              <FontAwesomeIcon icon={faTruck} /> Assign Driver
            </button>
          )}
          {selectedStakeholder === "Mechanic" && (
            <button className="w-48 text-sm text-left px-3 py-2 bg-gray-700 rounded flex items-center gap-3 text-white hover:bg-yellow-700 transition" onClick={handleRepairStatus}>
              <FontAwesomeIcon icon={faWrench} /> Update Repair
            </button>
          )}
          {selectedStakeholder === "Vigilance" && (
            <button className="w-48 text-sm text-left px-3 py-2 bg-gray-700 rounded flex items-center gap-3 text-white hover:bg-purple-700 transition" onClick={handleFine}>
              <FontAwesomeIcon icon={faGavel} /> Issue Fine
            </button>
          )}
          {selectedStakeholder === "Merchant" && (
            <button className="w-48 text-sm text-left px-3 py-2 bg-gray-700 rounded flex items-center gap-3 text-white hover:bg-emerald-700 transition" onClick={handleCargoUpdate}>
              <FontAwesomeIcon icon={faTruck} /> Update Cargo
            </button>
          )}
          {selectedStakeholder === "Municipal Officer" && (
            <button className="w-48 text-sm text-left px-3 py-2 bg-gray-700 rounded flex items-center gap-3 text-white hover:bg-amber-700 transition" onClick={handleCollectionUpdate}>
              <FontAwesomeIcon icon={faRecycle} /> Log Collection
            </button>
          )}
          {["Local Trains", "Metro", "Bus"].includes(selectedTransportMode) && (
            <button className="w-48 text-sm text-left px-3 py-2 bg-gray-700 rounded flex items-center gap-3 text-white hover:bg-indigo-700 transition" onClick={handleSync}>
              <FontAwesomeIcon icon={faMapMarkerAlt} /> Live Status
            </button>
          )}
        </div>
        <div className="mt-4">
          <h4 className="text-sm text-yellow-200 font-semibold mb-1">News / Alerts</h4>
          <div className="space-y-1 max-h-20 overflow-y-auto text-xs text-orange-400">
            {alerts.slice(0, 3).map((alert, i) => <div key={i} className="p-1 bg-orange-900/30 rounded">• {alert}</div>)}
          </div>
        </div>
        <Notifications />
      </div>
    </div>
  );

const Notifications = () => (
  <>
    {showSOS && (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
        <div className="bg-red-700 text-white px-8 py-6 rounded-lg shadow-lg text-center animate-pulse">
          <h2 className="text-xl font-bold mb-2">🚨 SOS Activated</h2>
          <p>This is a test alert. System functioning correctly.</p>
        </div>
      </div>
    )}
    {notifications.length > 0 && (
      <div className="mt-4 bg-gray-700 rounded p-2 text-xs text-gray-200 max-h-32 overflow-y-auto">
        <h4 className="text-sm text-yellow-200 font-semibold mb-1">Activity Log:</h4>
        <ul className="space-y-1">
          {notifications.map((note, i) => (
            <li key={i}>• {note}</li>
          ))}
        </ul>
      </div>
    )}
  </>
);

const renderLiveMap = () => {
  const transportIcons = {
    "Local Trains": faTrain,
    Metro: faSubway,
    Ferry: faShip,
    "Taxi/ Auto": faTaxi,
    Bus: faBus,
  };

  const modeIcon = transportIcons[selectedTransportMode] || faBus;

  const specialSections = [
    "Fare Calculator",
    "Favorites",
    "Nearby Services",
    "My Profile",
    "Emergency",
  ];

  if (specialSections.includes(selectedTransportMode)) {
    return (
      <div className="lg:col-span-2 bg-gray-800 rounded p-3 h-64 flex items-center justify-center text-gray-400">
        {selectedTransportMode}
      </div>
    );
  }

  if (selectedTransportMode === "Bus") {
    return (
      <div className="lg:col-span-2 bg-gray-800 rounded p-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold flex items-center gap-2 text-yellow-200">
            <FontAwesomeIcon icon={modeIcon} />
            {selectedTransportMode} Live View
          </h2>
        </div>
        <BusModule
          vehicles={filteredVehicles.filter(v => v.type === "Bus")}
          selectedStakeholder={selectedStakeholder}
          onOpenVehicle={handleOpenVehicle}
          onSellTicket={sellTicket}
          onToggleFavorite={toggleFavorite}
        />
      </div>
    );
  }

  return (
    <div className="lg:col-span-2 bg-gradient-to-br from-gray-800 to-gray-700 rounded p-3 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <FontAwesomeIcon icon={modeIcon} />
          {selectedTransportMode} Live View
        </h2>
        <div className="text-xs text-gray-400">Dummy Data • AI Predictions</div>
      </div>

      <div className="relative w-full h-64 rounded overflow-hidden bg-black/40 border border-gray-700 flex flex-col md:flex-row">
        {/* MAP */}
        <div className="w-full md:w-2/3 h-56 md:h-auto p-2">
          <div className="relative w-full h-full border border-dashed border-gray-700 rounded">
            {filteredVehicles.map((v, i) => (
              <div
                key={v.id}
                className={`absolute cursor-pointer -translate-x-1/2 -translate-y-1/2 p-1 rounded-full text-xs flex items-center gap-1 ${
                  v.type === "Local Train"
                    ? "bg-blue-500"
                    : v.type === "Metro"
                    ? "bg-purple-500"
                    : v.type === "Ferry"
                    ? "bg-cyan-500"
                    : v.type === "Taxi" || v.type === "Auto"
                    ? "bg-yellow-500"
                    : "bg-indigo-500"
                }`}
                style={{
                  top: `${20 + (i * 18) % 80}%`,
                  left: `${10 + (i * 23) % 80}%`,
                }}
                onClick={() => handleOpenVehicle(v)}
              >
                <div className="hidden md:block text-[10px]">{v.id}</div>
              </div>
            ))}
          </div>
        </div>

        {/* LIST */}
        <div className="w-full md:w-1/3 p-2 border-l border-gray-700">
          <h3 className="text-xs font-semibold mb-2">
            Nearby {selectedTransportMode}
          </h3>

          <div className="space-y-2 overflow-auto h-full">
            {filteredVehicles.map(v => (
              <div
                key={v.id}
                className="bg-gray-800 p-2 rounded flex justify-between"
              >
                <div>
                  <div className="text-sm font-medium">
                    {v.id} • {v.route}
                  </div>
                  <div className="text-xs text-gray-400">
                    Status: <span className="font-semibold">{v.status}</span>
                  </div>
                </div>
                <button
                  className="px-2 py-1 bg-indigo-600 rounded text-xs"
                  onClick={() => handleOpenVehicle(v)}
                >
                  View
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const renderUtility = () => {
  // Special modes
  if (selectedTransportMode === "Fare Calculator") {
    return (
      <aside className="bg-gray-800 rounded p-3 max-h-[90vh] overflow-y-auto">
        <h3 className="text-sm font-semibold mb-2">Fare Calculator</h3>
        <div className="space-y-2">
          <input placeholder="From (e.g., Churchgate)" className="w-full bg-gray-700 px-2 py-2 rounded text-sm" value={fareFrom} onChange={e => setFareFrom(e.target.value)} />
          <input placeholder="To (e.g., Virar)" className="w-full bg-gray-700 px-2 py-2 rounded text-sm" value={fareTo} onChange={e => setFareTo(e.target.value)} />
          <select className="w-full bg-gray-700 px-2 py-2 rounded text-sm" value={selectedTransportMode} onChange={e => setSelectedTransportMode(e.target.value)}>
            <option>Local Trains</option>
            <option>Metro</option>
            <option>Bus</option>
            <option>Auto</option>
          </select>
          <button className="w-full bg-indigo-600 py-2 rounded" onClick={calculateFare}>Calculate</button>
          {calculatedFare && <div className="text-sm text-green-400">Total: ₹{calculatedFare.total} - {calculatedFare.breakdown}</div>}
        </div>
      </aside>
    );
  }
  if (selectedTransportMode === "Favorites") {
    return (
      <aside className="bg-gray-800 rounded p-3 max-h-[90vh] overflow-y-auto">
        <h3 className="text-sm font-semibold mb-2">Favorites</h3>
        <ul className="space-y-1 text-xs">
          {favorites.map((fav, i) => (
            <li key={i} className="flex justify-between p-1 bg-gray-700 rounded">
              {fav} <button onClick={() => toggleFavorite(fav)} className="text-red-400">✕</button>
            </li>
          ))}
        </ul>
      </aside>
    );
  }
  if (selectedTransportMode === "Nearby Services") {
    return (
      <aside className="bg-gray-800 rounded p-3 max-h-[90vh] overflow-y-auto">
        <h3 className="text-sm font-semibold mb-2">Nearby Services</h3>
        <div className="space-y-2 text-xs">
          <div><strong>Hotels:</strong> {nearbyServices.hotels.join(", ")}</div>
          <div><strong>Hospitals:</strong> {nearbyServices.hospitals.join(", ")}</div>
          <div><strong>Attractions:</strong> {nearbyServices.attractions.join(", ")}</div>
          <div><strong>Airports:</strong> {nearbyServices.airports.join(", ")}</div>
        </div>
      </aside>
    );
  }
  if (selectedTransportMode === "My Profile") {
    return (
      <aside className="bg-gray-800 rounded p-3 max-h-[90vh] overflow-y-auto">
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><FontAwesomeIcon icon={faUser} /> My Profile</h3>
        <div className="text-xs space-y-2">
          <div>Preferences: Eco-friendly routes</div>
          <div>Saved Tickets: {bookingsHistory.length}</div>
          <button className="w-full bg-gray-700 py-2 rounded" onClick={() => addNotification("Profile updated")}>Edit</button>
        </div>
      </aside>
    );
  }
  if (selectedTransportMode === "Emergency") {
    return (
      <aside className="bg-gray-800 rounded p-3 max-h-[90vh] overflow-y-auto">
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><FontAwesomeIcon icon={faPhone} /> Helplines</h3>
        <div className="text-xs space-y-2">
          <div>Police: {helplines.police}</div>
          <div>Medical: {helplines.medical}</div>
          <div>Railway: {helplines.railway}</div>
          <button className="w-full bg-red-600 py-2 rounded" onClick={() => handleSOS()}>Call Emergency</button>
        </div>
      </aside>
    );
  }
  
  // Default: Stakeholder-specific
  switch (selectedStakeholder) {
    case "Passenger":
      return (
        <RightPassenger 
          booking={booking} 
          setBooking={setBooking} 
          bookingsHistory={bookingsHistory} 
          vehicles={filteredVehicles} 
          handleBookNow={handleBookNow} 
        />
      );
    case "Admin":
      return <StakeholderOverview title="Admin Panel" desc="Master config, seating layout, reports" icon={faFileAlt} actions={["Generate Full Report", "Configure Routes"]} onAction={handleReport} />;
    case "Fleet Manager":
      return <StakeholderOverview title="Fleet Management" desc="Live status, assign drivers, SOS" icon={faTruck} actions={["Assign Driver", "View SOS Alerts"]} onAction={handleAssignDriver} />;
    case "Driver":
      return <StakeholderOverview title="Driver Dashboard" desc="Start journey, update stops, SOS" icon={faMapMarkerAlt} actions={["Start Journey", "Update Stop"]} onAction={handleSync} />;
    case "Attendant":
      return <StakeholderOverview title="Attendant Tools" desc="Sell tickets, update seating" icon={faTicketAlt} actions={["Sell Ticket", "Update Seats"]} onAction={sellTicket} />;
    case "Mechanic":
      return <StakeholderOverview title="Mechanic Portal" desc="Breakdown & repair status" icon={faWrench} actions={["Log Breakdown", "Update Repair"]} onAction={handleRepairStatus} />;
    case "Service Center":
      return <StakeholderOverview title="Service Center" desc="Service scheduling & tracking" icon={faWrench} actions={["Schedule Service", "Track Repairs"]} onAction={handleRepairStatus} />;
    case "Vigilance":
      return <StakeholderOverview title="Vigilance Control" desc="Fines, compliance checks" icon={faUserShield} actions={["Issue Fine", "Compliance Check"]} onAction={handleFine} />;
    case "Merchant":
      return <StakeholderOverview title="Merchant Tracking" desc="Cargo tracking, ETA updates" icon={faTruck} actions={["Track Shipment", "Update ETA"]} onAction={handleCargoUpdate} />;
    case "Municipal Officer":
      return <StakeholderOverview title="Municipal Ops" desc="Garbage routes, collection logs" icon={faRecycle} actions={["Log Collection", "Route Planning"]} onAction={handleCollectionUpdate} />;
    default:
      return <StakeCards />;
  }
};

const RightPassenger = ({ booking, setBooking, bookingsHistory, vehicles, handleBookNow }) => {
  const [showSeatMap, setShowSeatMap] = useState(false);
  const [selectedSeat, setSelectedSeat] = useState(null);

  const selectedVehicle = vehicles.find(v => v.id === booking.vehicleId);
  const requiresSeatSelection = selectedVehicle && ['Bus', 'Ferry'].includes(selectedVehicle.type);
  const hasSeatSelected = requiresSeatSelection ? !!selectedSeat : true;
  const numSeats = requiresSeatSelection ? 1 : booking.seats;

  const handleSeatSelect = (seatNumber) => {
    setSelectedSeat(seatNumber);
    setBooking(prev => ({ ...prev, selectedSeat: seatNumber, seats: 1 }));
    setShowSeatMap(false);
  };

  const handleBookNowLocal = () => {
    if (requiresSeatSelection && !selectedSeat) {
      alert("Please select a seat first.");
      return;
    }
    handleBookNow();
  };

  const generateSeats = () => {
    const seats = [];
    for (let i = 1; i <= 20; i++) {
      const isUnavailable = i % 5 === 0; // Demo: every 5th seat unavailable
      seats.push(
        <div
          key={i}
          className={`p-1 text-center text-xs rounded cursor-pointer transition-colors ${
            isUnavailable
              ? 'bg-gray-500 text-gray-300'
              : selectedSeat === i
              ? 'bg-blue-400 text-white'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
          onClick={() => !isUnavailable && handleSeatSelect(i)}
        >
          {i}
        </div>
      );
    }
    return seats;
  };

  return (
    <aside className="bg-gray-800 rounded p-3 max-h-[90vh] overflow-y-auto">
      <h3 className="text-sm font-semibold mb-2">Quick Booking</h3>
      <div className="space-y-2 mb-4 gap-2">
        <select
          className="w-full h-10 bg-gray-700 px-2 py-2 rounded text-sm"
          value={booking.vehicleId || ""}
          onChange={e => {
            setBooking(b => ({ ...b, vehicleId: e.target.value }));
            setSelectedSeat(null); // Reset seat selection
          }}
        >
          <option value="">Select Vehicle</option>
          {vehicles.map(v => (
            <option key={v.id} value={v.id}>
              {v.id} • {v.route}
            </option>
          ))}
        </select>

        {requiresSeatSelection && (
          <button
            className="w-full bg-green-600 px-3 py-2 rounded text-white hover:bg-green-700 transition"
            onClick={() => setShowSeatMap(true)}
          >
            Select Seat
          </button>
        )}

        {showSeatMap && requiresSeatSelection && (
          <div className="bg-gray-900 p-3 rounded mb-2">
            <h4 className="text-xs font-semibold mb-2 text-center">Seat Map (Demo)</h4>
            <div className="grid grid-cols-5 gap-1">
              {generateSeats()}
            </div>
            <div className="text-xs text-gray-400 mt-2 text-center">
              Blue: Available | Grey: Unavailable
            </div>
            {selectedSeat && (
              <div className="text-xs text-green-400 mt-1 text-center">
                Selected: Seat {selectedSeat}
              </div>
            )}
          </div>
        )}

        {!requiresSeatSelection && (
          <input
            type="number"
            min={1}
            className="h-10 w-16 flex-1 bg-gray-700 px-2 py-2 rounded"
            value={booking.seats}
            onChange={e => setBooking(b => ({ ...b, seats: Number(e.target.value) }))}
            placeholder="Seats"
          />
        )}

        <button
          className={`ml-2 text-xs w-20 lg:text-sm lg:w-24 flex-1 px-2 py-2 rounded ${
            hasSeatSelected ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-500 cursor-not-allowed'
          } text-white transition`}
          onClick={handleBookNowLocal}
          disabled={!hasSeatSelected}
        >
          Book Now
        </button>

        <div className="text-xs text-gray-400">
          Recent Bookings: {bookingsHistory.length}
        </div>
      </div>

      <h4 className="text-xs font-semibold mb-2">Booking History</h4>
      <div className="space-y-1 max-h-32 overflow-y-auto text-xs">
        {bookingsHistory.slice(0, 5).map(b => (
          <div key={b.id} className="p-1 bg-gray-700 rounded">
            {b.vehicleId} - {b.route} ({b.seats} seats)
            {b.selectedSeat && ` | Seat: ${b.selectedSeat}`}
          </div>
        ))}
      </div>
    </aside>
  );
};

const StakeholderOverview = ({ title, desc, icon, actions, onAction }) => (
  <aside className="bg-gray-800 rounded p-3 max-h-[90vh] overflow-y-auto">
    <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
      <FontAwesomeIcon icon={icon} className="text-indigo-400" /> {title}
    </h3>
    <p className="text-xs text-gray-400 mb-3">{desc}</p>
    <div className="space-y-2">
      {actions.map((action, i) => (
        <button
          key={i}
          className="w-full text-left px-3 py-2 bg-gray-700 rounded flex items-center gap-2 text-white hover:bg-indigo-700 transition"
          onClick={onAction}
        >
          • {action}
        </button>
      ))}
    </div>
  </aside>
);

const StakeCards = () => (
  <aside className="bg-gray-800 rounded p-3 max-h-[90vh] overflow-y-auto">
    <h3 className="text-sm font-semibold">Stakeholder Map</h3>
    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
      <StakeCard title="Admin" desc="Master config, seating layout, reports" />
      <StakeCard title="Fleet Manager" desc="Live status, assign drivers, SOS" />
      <StakeCard title="Driver" desc="Start journey, update stops, SOS" />
      <StakeCard title="Attendant" desc="Sell tickets, update seating" />
      <StakeCard title="Mechanic" desc="Breakdown & repair status" />
      <StakeCard title="Service Center" desc="Service Center" />
      <StakeCard title="Vigilance" desc="Fines, compliance" />
      <StakeCard title="Passenger" desc="Search, book, track, feedback" />
      <StakeCard title="Merchant" desc="Cargo tracking, ETA" />
      <StakeCard title="Municipal" desc="Garbage routes, collection" />
    </div>
  </aside>
);

const renderBottomSection = () => (
  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
    <div className="md:col-span-2 bg-gray-800 p-3 rounded">
      <h4 className="text-sm font-semibold mb-2">Activity Feed (AI Insights)</h4>
      <div className="space-y-1 max-h-48 overflow-auto text-sm text-gray-300">
        {notifications.map((n, i) => <div key={i} className="p-2 bg-gray-900/50 rounded">{n}</div>)}
      </div>
    </div>
    {selectedStakeholder === "Driver" && (
      <DriverControls vehicles={filteredVehicles.filter(v => v.type === "Bus")} updateTrafficStatus={updateTrafficStatus} />
    )}
    {(selectedStakeholder === "Admin" || selectedStakeholder === "Fleet Manager") && (
      <div className="bg-gray-800 p-3 rounded">
        <h4 className="text-sm font-semibold mb-2">Fleet Controls</h4>
        <div className="flex flex-wrap gap-2">
          <button className="px-3 py-2 bg-green-600 rounded text-xs" onClick={handleReport}>Full Report</button>
          <button className="px-3 py-2 bg-indigo-600 rounded text-xs" onClick={handleAssignDriver}>Assign All</button>
        </div>
      </div>
    )}
  </div>
);

const DriverControls = ({ vehicles, updateTrafficStatus }) => (
  <div className="bg-gray-800 p-3 rounded">
    <h4 className="text-sm font-semibold mb-2">Traffic Controls</h4>
    <div className="flex flex-wrap gap-2 mb-2">
      {vehicles.map(v => (
        <div key={v.id} className="bg-gray-900 p-2 rounded w-full">
          <div className="flex items-center justify-between text-sm">
            <div>{v.id}</div>
            <div className="flex gap-1">
              <button className="px-2 py-1 bg-yellow-600 rounded text-xs" onClick={() => updateTrafficStatus(v.id, "Traffic Jam")}>Jam</button>
              <button className="px-2 py-1 bg-orange-600 rounded text-xs" onClick={() => updateTrafficStatus(v.id, "Moderate Traffic")}>Moderate</button>
              <button className="px-2 py-1 bg-green-600 rounded text-xs" onClick={() => updateTrafficStatus(v.id, "Road Clear")}>Clear</button>
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="text-xs text-gray-400">Indicate delay to improve ETA accuracy.</div>
  </div>
);

const renderModals = () => (
  <>
    {showSeatMap && activeVehicle && (
      <VehicleModal vehicle={activeVehicle} onClose={() => { setShowSeatMap(false); setActiveVehicle(null); }} onSOS={() => handleSOS(activeVehicle)} />
    )}
    {showSOS && (
      <Modal onClose={() => setShowSOS(false)}>
        <div className="p-4">
          <h3 className="text-lg font-semibold">SOS Test Mode</h3>
          <p className="text-sm text-gray-300 mt-2">This is a simulated SOS flow. In production this would notify fleet manager, mechanic and emergency contacts.</p>
          <div className="mt-3 flex gap-2">
            <button className="px-3 py-2 bg-red-600 rounded" onClick={() => { setShowSOS(false); addNotification("SOS confirmed (dummy)"); }}>Acknowledge</button>
            <button className="px-3 py-2 bg-gray-700 rounded" onClick={() => setShowSOS(false)}>Close</button>
          </div>
        </div>
      </Modal>
    )}
  </>
);

const VehicleModal = ({ vehicle, onClose, onSOS }) => (
  <Modal onClose={onClose}>
    <div className="p-3">
      <h3 className="text-lg font-semibold mb-2">{vehicle.id} • {vehicle.route}</h3>
      <div className="text-sm text-gray-300 mb-2">Status: {vehicle.status} • Next: {vehicle.nextArrival || 'N/A'}</div>
      {vehicle.crowdPrediction && <div className="text-sm mb-2">AI Crowd: {vehicle.crowdPrediction} | Delay: {vehicle.delayForecast}</div>}
      {/* Type-specific content */}
      {vehicle.type === "Local Train" && <div>Platform: 1 | Coach: 12</div>}
      {vehicle.type === "Ferry" && <div>Duration: {vehicle.duration}</div>}
      {vehicle.type === "Taxi" && <div>ETA: {vehicle.eta} | Fare: ₹{vehicle.estimatedFare}</div>}
      {vehicle.type === "Bus" && (
        <div className="grid grid-cols-4 gap-1 mb-3">
          {Array.from({ length: 20 }).map((_, i) => (
            <div key={i} className={`p-1 text-center text-xs rounded ${i % 5 === 0 ? 'bg-red-600' : 'bg-gray-700'}`}>S{i+1}</div>
          ))}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <button className="px-3 py-2 bg-indigo-600 rounded" onClick={onSOS}>SOS</button>
        <button className="px-3 py-2 bg-gray-700 rounded" onClick={onClose}>Close</button>
      </div>
    </div>
  </Modal>
);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4">
      {renderHeader()}
      <div className="mt-2 py-2 grid grid-cols-4 lg:grid-cols-6 gap-1 place-items-center">
        {["Bus", "Taxi/ Auto", "Metro", "Local Trains", "Ferry"].map(mode => {
          const icon = transportIcons[mode];
          const isSelected = selectedTransportMode === mode;
          return (
            <button
              key={mode}
              onClick={() => setSelectedTransportMode(mode)}
              className={`w-full p-2 rounded text-center transition-colors ${isSelected ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
            >
              <FontAwesomeIcon icon={icon} className="text-base" />
              <div className="text-xs leading-tight">{mode}</div>
            </button>
          );
        })}
      </div>
      {renderSearchAndStats()}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {renderLiveMap()}
        {renderUtility()}
      </div>
      {renderBottomSection()}
      {renderModals()}
    </div>
  );
  // if (selectedStakeholder === "Driver" && selectedTransportMode === "Bus") {
  //   return (
  //     <div className="w-full h-screen">
  //       <DriverTripProvider>
  //         <DriverControls
  //           onTripEnd={() => {
  //             // Reset driver view state, show passenger view
  //             setSelectedStakeholder(null);
  //             setSelectedTransportMode("Bus");
  //           }}
  //         />
  //       </DriverTripProvider>
  //     </div>
  //   );
  // }
}

// Presentational components
function StatCard({ title, value, icon }) {
  return (
    <div className="bg-gray-800 p-3 rounded flex items-center gap-3">
      <div className="text-sm lg:text-xl p-2 bg-gray-700 rounded">{icon}</div>
      <div>
        <div className="text-xs lg:text-sm text-gray-400">{title}</div>
        <div className="text-sm lg:text-lg font-semibold">{value}</div>
      </div>
    </div>
  );
}

function StakeCard({ title, desc }) {
  return (
    <div className="bg-gray-900 p-2 rounded border border-gray-700">
      <div className="text-sm font-medium">{title}</div>
      <div className="text-xs text-gray-400">{desc}</div>
    </div>
  );
}

function Modal({ children, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose}></div>
      <div className="relative max-w-2xl w-full bg-gray-900 border border-gray-700 rounded p-3">
        {children}
      </div>
    </div>
  );
}